import React, { useMemo } from 'react';

import { Button } from '@components/Button';
import { TagSearchInput } from '@components/Forms/TagSearchInput';
import { TagSearchInputMobxZodControl } from '@components/Forms/TagSearchInputStore';
import { InputOptions, TextInput } from '@components/NextForms/TextInput';
import { preAlertStrings } from '@data/preAlert';
import { useSearch } from '@hooks/next/useSearch';
import {
  DashboardPreAlertSearchFieldName,
  DashboardPreAlertSearchType,
} from '@models/next/PreAlert';
import { empty } from '@monoid-dev/mobx-zod-form';
import { useForm } from '@monoid-dev/mobx-zod-form-react';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { formStrings } from '@utils/forms';
import { getFieldLabel } from '@utils/nextForms';
import { pick } from 'remeda';

const useStyles = makeMuiStyles((theme, common) => ({
  tagSearchInputContainer: {
    ...common.middleText,
    display: 'grid',
    rowGap: 20,
    marginTop: '1rem',
    marginBottom: '1rem',
    backgroundColor: theme.palette.grey[50],
    padding: '1rem 1rem',
  },
  searchRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr 30px 1fr 60px)',
    marginRight: '-30px',
    columnGap: 8,
    rowGap: 20,
  },
  searchTailLabel: {
    ...common.middleText,
    ...common.textGray700,
    textAlign: 'start',
    paddingTop: 5,
  },
  buttonContainer: {
    ...common.flexInline,
    columnGap: '0.5rem',
  },
}));

interface BookingsSearchFormProps {
  tagFields: DashboardPreAlertSearchFieldName[];
  placeholder?: React.ReactNode;
  fieldsInputOptions?: Partial<Record<DashboardPreAlertSearchFieldName, InputOptions>>;
  rangeFields?: (
    | 'eta'
    | 'etd'
    | 'delivery_instruction_preferred_delivery_date'
    | 'delivery_date'
    | 'delivery_request_pickup_date'
    | 'delivery_request_delivery_date'
  )[];
}

export const BookingsSearchForm: React.FC<BookingsSearchFormProps> = ({
  tagFields,
  placeholder,
  fieldsInputOptions,
  rangeFields,
}) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };
  const { search, updateSearch } = useSearch(DashboardPreAlertSearchType);

  const initialSearch = {
    ...search,
    page: search.page ?? 1,
    page_size: search.page_size ?? 50,
  };
  const form = useForm(DashboardPreAlertSearchType, {
    initialOutput: initialSearch,
    enableReinitialize: true,
  });

  const { fields } = form.root;

  const controller = useMemo(
    () => new TagSearchInputMobxZodControl(pick(fields, tagFields), form, fieldsInputOptions),
    [fieldsInputOptions, tagFields],
  );

  const renderRangeFields = () => {
    return rangeFields?.map((rangeName) => {
      return (
        <React.Fragment key={rangeName}>
          <div>
            <TextInput
              field={fields[`${rangeName}_after`]}
              inputProps={{ placeholder: getFieldLabel(fields[`${rangeName}_after`]) }}
            />
          </div>
          <label className={classes.searchTailLabel}>{formStrings.from}</label>
          <div>
            <TextInput
              field={fields[`${rangeName}_before`]}
              inputProps={{ placeholder: getFieldLabel(fields[`${rangeName}_before`]) }}
            />
          </div>
          <label className={classes.searchTailLabel}>{formStrings.until}</label>
        </React.Fragment>
      );
    });
  };

  return (
    <form
      {...form.bindForm({
        onSubmit(d) {
          updateSearch({ ...d, page: 1 });
        },
      })}
      className={classes.tagSearchInputContainer}
    >
      <TagSearchInput
        controller={controller}
        placeholder={placeholder ?? preAlertStrings.tagSearchInputPlaceholder}
        styles={{
          wrapper: {
            gridColumn: '1 / -1',
          },
        }}
      />
      <div className={classes.searchRow}>{renderRangeFields()}</div>

      <div className={classes.buttonContainer}>
        <Button
          color="primary"
          shape="round"
          text={formStrings.filter}
          type="submit"
          size="small"
        />
        <Button
          color="primary"
          shape="round"
          text={formStrings.reset}
          type="submit"
          size="small"
          onClick={() => {
            form.root.setOutput(empty);
          }}
        />
      </div>
    </form>
  );
};
